import {
	SessionCreatedEvent,
	SessionServiceReadyEvent
} from '@studyportals/student-interfaces';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import posthog from 'posthog-js';
import { Product, Tracker } from '@studyportals/datalake-event-tracker';
import {
	IExperimentTrackingData
} from '@studyportals/datalake-event-tracker/dist/src/interfaces/tracking-data.interface';
import { PortalType } from "@studyportals/authentication-service-interfaces";
import { DataLayerPageViewDispatched } from "@studyportals/datalayer";

declare global {
	interface Window {
		PostHog: import('posthog-js').PostHog;
		EventAggregationService: import('@studyportals/event-aggregation-service-interface').IEventAggregationService;
	}
}

/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
declare const POSTHOG_PUBLIC_API_KEY: string;
declare const POSTHOG_EXPERIMENT_KEY: string;
export default class PostHog implements ISubscriber<SessionCreatedEvent | SessionServiceReadyEvent> {
	private readonly tracker: Tracker;
	private wasUserIdentifiedInPostHog = false;

	constructor() {
		this.tracker = new Tracker(Product.EXPERIMENT, true, true);

		this.initPostHog();
		this.subscribeToEvents();
		this.attachEventToFeatureFlags();
	}

	public notify(event: SessionCreatedEvent | SessionServiceReadyEvent |  DataLayerPageViewDispatched ): void {
		if (event.eventType === SessionCreatedEvent.EventType) {
			this.handleSessionCreatedEvent(event as SessionCreatedEvent);
			return;
		}

		if( event.eventType === SessionServiceReadyEvent.EventType) {
			void this.handleSessionServiceReadyEvent(event as SessionServiceReadyEvent);
		}

		if (event.eventType === DataLayerPageViewDispatched.EventType) {
			const data = event as DataLayerPageViewDispatched;
			let setObject = {}
			if(data.user && data.user.nationality){
				setObject = { $set: { nationality_country_iso: data.user.nationality } };
			}
			window.PostHog.capture("$pageview", event, setObject);
		}
	}

	private subscribeToEvents(): void {
		window.EventAggregationService?.subscribeTo(SessionCreatedEvent.EventType, this, true);
		window.EventAggregationService?.subscribeTo(SessionServiceReadyEvent.EventType, this, true);
		window.EventAggregationService?.subscribeTo(DataLayerPageViewDispatched.EventType, this, true);
	}

	private initPostHog(): void {
		const instance = posthog.init(POSTHOG_PUBLIC_API_KEY, {
			api_host: this.apiHost,
			ui_host: 'https://eu.posthog.com',
			person_profiles: 'identified_only',
			capture_pageview: false,
			capture_pageleave: true,
		}, 'Portal');

		if (!instance) {
			throw new Error('PostHog failed to initialize');
		}

		window.PostHog = instance;
	}

	private handleSessionCreatedEvent(event: SessionCreatedEvent): void {
		if(this.wasUserIdentifiedInPostHog) {
			return;
		}

		const {session} = event;
		const {identityId} = session.getUser();

		window.PostHog.identify(identityId);
		this.wasUserIdentifiedInPostHog = true;
		this.sendReadyEvent();
	}

	private async handleSessionServiceReadyEvent(event: SessionServiceReadyEvent): Promise<void> {
		const session = await event.sessionService.getSession();

		if (!session) {
			return;
		}

		window.PostHog.identify(session.getUser().identityId);
		this.wasUserIdentifiedInPostHog = true;
		this.sendReadyEvent();
	}

	private sendReadyEvent(): void {
		document.dispatchEvent(new CustomEvent('PostHogReady'));
	}

	private attachEventToFeatureFlags(): void {
		window.PostHog.onFeatureFlags((flags, variants) => {
			this.evaluateFeatureFlags(flags, variants);
		});
	}

	private evaluateFeatureFlags(flags: string[], variants: Record<string, string | boolean>): void {
		for (const experiment of Object.values(flags)) {
			const isFlagExperimentType = experiment.includes(POSTHOG_EXPERIMENT_KEY);
			if (!isFlagExperimentType) continue;

			const variation = variants[experiment];
			const variationIndex = variation === 'control'
				? 0
				: parseInt((variation as string).match(/\d+/)?.[0] ?? '0', 10);

			this.trackExperimentEvent(experiment, variationIndex);
		}
	}

	private trackExperimentEvent(experiment: string, variationIndex: number): void {
		const trackingData: IExperimentTrackingData = {
			category: Product.EXPERIMENT,
			action: `${experiment} - ${variationIndex}`,
			label: 'ExperimentImpression',
		};

		this.tracker.trackExperimentEvent(trackingData);
	}

	private get apiHost(): string {
		let domain: string;

		switch (window.PortalType as PortalType) {

		case PortalType.PHD:
			domain = 'phdportal.com';
			break;

		case PortalType.BACHELORS:
			domain = 'bachelorsportal.com';
			break;

		case PortalType.MASTERS:
		default:
			domain = 'mastersportal.com';
			break;
		}

		return `https://watermelon.${domain}`;
	}
}

new PostHog();





